/**
 * Spearman Renderer - Specialized renderer for spearman units with thrust attack animation
 * Features:
 * - Procedural spear thrust attack animation via custom shaders
 * - Wind-up, thrust, impact hold and recovery phases
 * - Body lunge and footwork during attack
 * - Walk animation with spear-ready stance
 */

import * as THREE from 'three';
import type { FactionId, Entity } from '@/types';
import { EventBus } from '@/core/events/EventBus';
import { COLORS, FACTION_COLORS } from './unitConstants';

const MAX_SPEARMEN = 800;

// Animation group IDs encoded in vertex attribute
export const SPEARMAN_ANIM_GROUP = {
  STATIC: 0,        // No animation (decorative elements)
  BODY: 1,          // Torso - lunges during thrust
  HEAD: 2,          // Head - follows body motion
  LEFT_ARM: 3,      // Left arm (lower grip on spear)
  RIGHT_ARM: 4,     // Right arm (upper grip on spear)
  LEFT_LEG: 5,      // Left leg - plants during thrust
  RIGHT_LEG: 6,     // Right leg - steps forward with thrust
  SPEAR: 7,         // Spear - thrusts forward
  LEFT_SHOULDER: 8, // Left shoulder armor
  RIGHT_SHOULDER: 9, // Right shoulder armor
  TASSEL: 10,       // Red tassel - sways dynamically
};

interface SpearmanData {
  entity: Entity;
  x: number;
  y: number;
  z: number;
  factionId: FactionId;
  rotation: number;
  speed: number;           // Movement speed for walk animation
  animPhase: number;       // Animation phase offset
  attackTime: number;      // Time when attack started (-1 = not attacking)
  attackDuration: number;  // Duration of attack animation
}

// Vertex shader with spear thrust animation
const spearmanVertexShader = `
  attribute vec3 color;
  attribute float animGroup;

  uniform float uTime;

  // Per-instance attributes
  attribute float instanceSpeed;
  attribute float instanceAnimPhase;
  attribute float instanceAttackTime;

  varying vec3 vColor;
  varying vec3 vNormal;
  varying vec3 vWorldPosition;

  // Animation parameters
  const float PI = 3.14159265359;
  const float WALK_FREQUENCY = 4.5;
  const float ATTACK_DURATION = 0.5;  // Attack animation duration in seconds

  // Rotation helper - rotate around Y axis
  vec3 rotateY(vec3 pos, float angle, vec3 pivot) {
    vec3 p = pos - pivot;
    float c = cos(angle);
    float s = sin(angle);
    return vec3(
      p.x * c + p.z * s,
      p.y,
      -p.x * s + p.z * c
    ) + pivot;
  }

  // Rotation helper - rotate around X axis
  vec3 rotateX(vec3 pos, float angle, vec3 pivot) {
    vec3 p = pos - pivot;
    float c = cos(angle);
    float s = sin(angle);
    return vec3(
      p.x,
      p.y * c - p.z * s,
      p.y * s + p.z * c
    ) + pivot;
  }

  vec3 animateVertex(vec3 pos, float group, float time, float speed, float phase, float attackTime) {
    vec3 animated = pos;

    // Calculate attack progress (0 = not attacking, 0-1 = attack progress)
    float attackProgress = 0.0;
    float attackPhase = 0.0;
    if (attackTime >= 0.0) {
      float timeSinceAttack = time - attackTime;
      if (timeSinceAttack < ATTACK_DURATION) {
        attackProgress = timeSinceAttack / ATTACK_DURATION;
        // Attack phases:
        // 0-0.2 = wind up (pull back)
        // 0.2-0.4 = thrust forward
        // 0.4-0.55 = impact hold
        // 0.55-1.0 = recover
        if (attackProgress < 0.2) {
          attackPhase = attackProgress / 0.2;  // 0-1 wind up
        } else if (attackProgress < 0.4) {
          attackPhase = 1.0 + (attackProgress - 0.2) / 0.2;  // 1-2 thrust
        } else if (attackProgress < 0.55) {
          attackPhase = 2.0 + (attackProgress - 0.4) / 0.15;  // 2-3 impact
        } else {
          attackPhase = 3.0 + (attackProgress - 0.55) / 0.45;  // 3-4 recover
        }
      }
    }

    // Walking animation
    float walkIntensity = clamp(speed, 0.0, 1.0);
    float t = time * WALK_FREQUENCY + phase;

    // Idle sway
    float idleSway = sin(time * 1.5 + phase) * 0.003 * (1.0 - walkIntensity);
    float idleBreath = sin(time * 1.0 + phase) * 0.002 * (1.0 - walkIntensity);

    // Body pivot point (center of torso)
    vec3 bodyPivot = vec3(0.0, 0.36, 0.0);
    // Shoulder pivot points (for arms)
    vec3 rightShoulderPivot = vec3(0.095, 0.42, 0.0);
    vec3 leftShoulderPivot = vec3(-0.10, 0.40, 0.0);
    // Spear grip pivot (between hands)
    vec3 spearPivot = vec3(0.12, 0.36, 0.05);

    if (group < 0.5) {
      // STATIC - no animation
      return animated;
    }

    // Apply idle sway to all animated parts
    animated.z += idleSway;

    if (group < 1.5) {
      // BODY - lunges during thrust
      float walkBob = sin(t * 2.0) * 0.003 * walkIntensity;
      animated.y += walkBob + idleBreath;

      if (attackPhase > 0.0) {
        float leanForward = 0.0;
        float lungeForward = 0.0;

        if (attackPhase < 1.0) {
          // Wind up - lean back slightly, pull body back
          leanForward = -0.01 * attackPhase;
          lungeForward = -0.015 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Thrust - lunge forward powerfully
          float thrustPhase = attackPhase - 1.0;
          float easeOut = 1.0 - (1.0 - thrustPhase) * (1.0 - thrustPhase);
          leanForward = -0.01 + 0.035 * easeOut;
          lungeForward = -0.015 + 0.06 * easeOut;
        } else if (attackPhase < 3.0) {
          // Impact hold - maintain forward position
          leanForward = 0.025;
          lungeForward = 0.045;
        } else {
          // Recover - return to neutral
          float recoverPhase = attackPhase - 3.0;
          leanForward = 0.025 * (1.0 - recoverPhase);
          lungeForward = 0.045 * (1.0 - recoverPhase);
        }

        animated.z += lungeForward;
        animated = rotateX(animated, leanForward, bodyPivot);
      }
    }
    else if (group < 2.5) {
      // HEAD - follows body, looks at target during thrust
      float walkBob = sin(t * 2.0 + 0.1) * 0.002 * walkIntensity;
      animated.y += walkBob + idleBreath;

      if (attackPhase > 0.0) {
        float leanForward = 0.0;
        float focusForward = 0.0;

        if (attackPhase < 1.0) {
          leanForward = -0.008 * attackPhase;
        } else if (attackPhase < 2.0) {
          float thrustPhase = attackPhase - 1.0;
          leanForward = -0.008 + 0.03 * thrustPhase;
          focusForward = 0.005 * thrustPhase;
        } else if (attackPhase < 3.0) {
          leanForward = 0.022;
          focusForward = 0.005;
        } else {
          float recoverPhase = attackPhase - 3.0;
          leanForward = 0.022 * (1.0 - recoverPhase);
          focusForward = 0.005 * (1.0 - recoverPhase);
        }

        animated = rotateX(animated, leanForward, bodyPivot);
        animated.z += focusForward;
      }
    }
    else if (group < 3.5) {
      // LEFT_ARM (lower grip) - follows spear motion
      float walkSwing = sin(t) * 0.006 * walkIntensity;
      animated.z += walkSwing * 0.3;

      if (attackPhase > 0.0) {
        float thrustForward = 0.0;
        float liftUp = 0.0;

        if (attackPhase < 1.0) {
          // Wind up - arm pulls back with spear
          thrustForward = -0.02 * attackPhase;
          liftUp = 0.005 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Thrust - arm pushes forward
          float thrustPhase = attackPhase - 1.0;
          float easeOut = 1.0 - (1.0 - thrustPhase) * (1.0 - thrustPhase);
          thrustForward = -0.02 + 0.08 * easeOut;
          liftUp = 0.005 - 0.005 * easeOut;
        } else if (attackPhase < 3.0) {
          // Hold
          thrustForward = 0.06;
          liftUp = 0.0;
        } else {
          // Recover
          float recoverPhase = attackPhase - 3.0;
          thrustForward = 0.06 * (1.0 - recoverPhase);
        }

        animated.z += thrustForward;
        animated.y += liftUp;
      }
    }
    else if (group < 4.5) {
      // RIGHT_ARM (upper grip) - leads the thrust
      float walkSwing = -sin(t) * 0.004 * walkIntensity;
      animated.z += walkSwing * 0.2;

      if (attackPhase > 0.0) {
        float thrustForward = 0.0;
        float liftUp = 0.0;

        if (attackPhase < 1.0) {
          // Wind up - arm pulls back
          thrustForward = -0.025 * attackPhase;
          liftUp = 0.01 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Thrust - arm extends forward
          float thrustPhase = attackPhase - 1.0;
          float easeOut = 1.0 - (1.0 - thrustPhase) * (1.0 - thrustPhase);
          thrustForward = -0.025 + 0.10 * easeOut;
          liftUp = 0.01 - 0.015 * easeOut;
        } else if (attackPhase < 3.0) {
          // Hold at extension
          thrustForward = 0.075;
          liftUp = -0.005;
        } else {
          // Recover
          float recoverPhase = attackPhase - 3.0;
          thrustForward = 0.075 * (1.0 - recoverPhase);
          liftUp = -0.005 * (1.0 - recoverPhase);
        }

        animated.z += thrustForward;
        animated.y += liftUp;
      }
    }
    else if (group < 5.5) {
      // LEFT_LEG - plants and braces during thrust
      float cycle = t;
      float lift = max(sin(cycle), 0.0) * 0.008 * walkIntensity;
      float swing = sin(cycle) * 0.006 * walkIntensity;
      animated.y += lift;
      animated.z += swing;

      if (attackPhase > 0.0) {
        float brace = 0.0;

        if (attackPhase < 1.0) {
          // Wind up - step back slightly
          brace = -0.01 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Thrust - plant firmly
          brace = -0.01;
        } else if (attackPhase < 3.0) {
          // Hold
          brace = -0.01;
        } else {
          // Recover
          float recoverPhase = attackPhase - 3.0;
          brace = -0.01 * (1.0 - recoverPhase);
        }
        animated.z += brace;
      }
    }
    else if (group < 6.5) {
      // RIGHT_LEG - steps forward with thrust
      float cycle = t + PI;
      float lift = max(sin(cycle), 0.0) * 0.008 * walkIntensity;
      float swing = sin(cycle) * 0.006 * walkIntensity;
      animated.y += lift;
      animated.z += swing;

      if (attackPhase > 0.0) {
        float stepForward = 0.0;
        float liftUp = 0.0;

        if (attackPhase < 1.0) {
          // Wind up - slight lift
          liftUp = 0.005 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Thrust - step forward with the attack
          float thrustPhase = attackPhase - 1.0;
          stepForward = 0.025 * thrustPhase;
          liftUp = 0.005 * (1.0 - thrustPhase);
        } else if (attackPhase < 3.0) {
          // Hold - foot planted forward
          stepForward = 0.025;
        } else {
          // Recover
          float recoverPhase = attackPhase - 3.0;
          stepForward = 0.025 * (1.0 - recoverPhase);
        }

        animated.z += stepForward;
        animated.y += liftUp;
      }
    }
    else if (group < 7.5) {
      // SPEAR - main thrust motion, extends much further than hands
      float idleSpearSway = sin(time * 0.7 + phase) * 0.002 * (1.0 - walkIntensity);
      animated.x += idleSpearSway;

      if (attackPhase > 0.0) {
        float thrustForward = 0.0;
        float tiltDown = 0.0;

        if (attackPhase < 1.0) {
          // Wind up - pull spear back, tip rises
          thrustForward = -0.06 * attackPhase;
          tiltDown = -0.03 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Thrust - spear shoots forward with power
          float thrustPhase = attackPhase - 1.0;
          float easeOut = 1.0 - (1.0 - thrustPhase) * (1.0 - thrustPhase);
          thrustForward = -0.06 + 0.22 * easeOut;
          tiltDown = -0.03 + 0.05 * easeOut;
        } else if (attackPhase < 3.0) {
          // Impact - hold at full extension
          float vibrate = sin(time * 60.0) * 0.002;
          thrustForward = 0.16 + vibrate;
          tiltDown = 0.02;
        } else {
          // Recover - pull back smoothly
          float recoverPhase = attackPhase - 3.0;
          float easeIn = recoverPhase * recoverPhase;
          thrustForward = 0.16 * (1.0 - easeIn);
          tiltDown = 0.02 * (1.0 - easeIn);
        }

        animated.z += thrustForward;
        // Apply tilt by rotating around spear pivot
        animated = rotateX(animated, tiltDown, spearPivot);
      }
    }
    else if (group < 8.5) {
      // LEFT_SHOULDER - follows body motion
      float walkBob = sin(t * 2.0 + 0.15) * 0.002 * walkIntensity;
      animated.y += walkBob + idleBreath;

      if (attackPhase > 0.0) {
        float leanForward = 0.0;

        if (attackPhase < 1.0) {
          leanForward = -0.008 * attackPhase;
        } else if (attackPhase < 2.0) {
          float thrustPhase = attackPhase - 1.0;
          leanForward = -0.008 + 0.025 * thrustPhase;
        } else if (attackPhase < 3.0) {
          leanForward = 0.017;
        } else {
          float recoverPhase = attackPhase - 3.0;
          leanForward = 0.017 * (1.0 - recoverPhase);
        }

        animated = rotateX(animated, leanForward, bodyPivot);
      }
    }
    else if (group < 9.5) {
      // RIGHT_SHOULDER - leads forward during thrust
      float walkBob = sin(t * 2.0 - 0.1) * 0.002 * walkIntensity;
      animated.y += walkBob + idleBreath;

      if (attackPhase > 0.0) {
        float leanForward = 0.0;
        float pushForward = 0.0;

        if (attackPhase < 1.0) {
          leanForward = -0.01 * attackPhase;
        } else if (attackPhase < 2.0) {
          float thrustPhase = attackPhase - 1.0;
          leanForward = -0.01 + 0.04 * thrustPhase;
          pushForward = 0.01 * thrustPhase;
        } else if (attackPhase < 3.0) {
          leanForward = 0.03;
          pushForward = 0.01;
        } else {
          float recoverPhase = attackPhase - 3.0;
          leanForward = 0.03 * (1.0 - recoverPhase);
          pushForward = 0.01 * (1.0 - recoverPhase);
        }

        animated = rotateX(animated, leanForward, bodyPivot);
        animated.z += pushForward;
      }
    }
    else if (group < 10.5) {
      // TASSEL - dynamic swaying, reacts to movement and attacks
      float tasselSway = sin(time * 3.0 + phase) * 0.01 * (0.5 + walkIntensity);
      float tasselBob = cos(time * 2.5 + phase) * 0.005;
      animated.x += tasselSway;
      animated.y += tasselBob;

      if (attackPhase > 0.0) {
        float drag = 0.0;
        float swing = 0.0;

        if (attackPhase < 1.0) {
          // Wind up - tassel swings forward (opposite to pull)
          drag = 0.02 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Thrust - tassel drags back from rapid forward motion
          float thrustPhase = attackPhase - 1.0;
          drag = 0.02 - 0.06 * thrustPhase;
        } else if (attackPhase < 3.0) {
          // Impact - tassel catches up and swings
          float impactPhase = attackPhase - 2.0;
          drag = -0.04 + 0.02 * impactPhase;
          swing = sin(impactPhase * PI * 2.0) * 0.015;
        } else {
          // Recover - settles down
          float recoverPhase = attackPhase - 3.0;
          drag = -0.02 * (1.0 - recoverPhase);
        }

        animated.z += drag;
        animated.x += swing;
      }
    }

    return animated;
  }

  void main() {
    vColor = color;
    vNormal = normalize(normalMatrix * normal);

    // Get instance-specific animation parameters
    float speed = instanceSpeed;
    float phase = instanceAnimPhase;
    float attackTime = instanceAttackTime;

    // Animate vertex
    vec3 animatedPosition = animateVertex(position, animGroup, uTime, speed, phase, attackTime);

    vec4 worldPosition = instanceMatrix * vec4(animatedPosition, 1.0);
    vWorldPosition = worldPosition.xyz;

    vec4 mvPosition = modelViewMatrix * worldPosition;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

// Fragment shader with faction colors and lighting
const spearmanFragmentShader = `
  uniform vec3 primaryColor;
  uniform vec3 secondaryColor;
  uniform vec3 ambientLightColor;
  uniform vec3 directionalLightColor;
  uniform vec3 directionalLightDirection;

  varying vec3 vColor;
  varying vec3 vNormal;
  varying vec3 vWorldPosition;

  void main() {
    vec3 finalColor = vColor;

    // Faction color replacement (red = primary, green = secondary)
    if (vColor.r > 0.9 && vColor.g < 0.2 && vColor.b < 0.2) {
      finalColor = primaryColor;
    } else if (vColor.g > 0.9 && vColor.r < 0.2 && vColor.b < 0.2) {
      finalColor = secondaryColor;
    }

    // Lighting
    vec3 normal = normalize(vNormal);
    vec3 lightDir = normalize(directionalLightDirection);

    float NdotL = dot(normal, lightDir);
    float diff = max(NdotL, 0.0);
    float wrap = max(NdotL * 0.5 + 0.5, 0.0);

    // Rim lighting for heroic effect
    vec3 viewDir = normalize(cameraPosition - vWorldPosition);
    float rim = 1.0 - max(dot(viewDir, normal), 0.0);
    rim = pow(rim, 4.0) * 0.18;

    // Combine lighting
    vec3 ambient = ambientLightColor * 0.5;
    vec3 diffuse = directionalLightColor * diff * 0.38;
    vec3 fill = directionalLightColor * wrap * 0.15;
    vec3 rimColor = vec3(1.0, 0.95, 0.9) * rim;

    vec3 litColor = finalColor * (ambient + diffuse + fill) + rimColor;

    // Slight color variation
    litColor *= 0.95 + 0.1 * fract(sin(dot(vWorldPosition.xz, vec2(12.9898, 78.233))) * 43758.5453);

    gl_FragColor = vec4(litColor, 1.0);
  }
`;

export class SpearmanRenderer {
  private scene: THREE.Scene;
  private instancedMeshes: Map<FactionId, THREE.InstancedMesh> = new Map();
  private spearmanData: SpearmanData[] = [];
  private entityMap: Map<Entity, SpearmanData> = new Map();
  private materials: Map<FactionId, THREE.ShaderMaterial> = new Map();
  private dummy = new THREE.Object3D();
  private time = 0;

  // Instance attributes for animation
  private speedAttribute: THREE.InstancedBufferAttribute | null = null;
  private phaseAttribute: THREE.InstancedBufferAttribute | null = null;
  private attackTimeAttribute: THREE.InstancedBufferAttribute | null = null;

  // Event listener cleanup
  private attackEventHandler: ((data: { attackerId: Entity }) => void) | null = null;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.createGeometryAndMeshes();
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Listen for attack events to trigger attack animation
    this.attackEventHandler = (data: { attackerId: Entity }) => {
      const spearman = this.entityMap.get(data.attackerId);
      if (spearman) {
        spearman.attackTime = this.time;
        spearman.attackDuration = 0.5;
      }
    };
    EventBus.on('unit:attack', this.attackEventHandler);
  }

  private createGeometryAndMeshes(): void {
    const geometry = this.createSpearmanGeometry();

    // Create instance attributes for animation
    const speeds = new Float32Array(MAX_SPEARMEN);
    const phases = new Float32Array(MAX_SPEARMEN);
    const attackTimes = new Float32Array(MAX_SPEARMEN);
    for (let i = 0; i < MAX_SPEARMEN; i++) {
      speeds[i] = 0;
      phases[i] = Math.random() * Math.PI * 2;
      attackTimes[i] = -1;  // Not attacking
    }

    this.speedAttribute = new THREE.InstancedBufferAttribute(speeds, 1);
    this.phaseAttribute = new THREE.InstancedBufferAttribute(phases, 1);
    this.attackTimeAttribute = new THREE.InstancedBufferAttribute(attackTimes, 1);

    geometry.setAttribute('instanceSpeed', this.speedAttribute);
    geometry.setAttribute('instanceAnimPhase', this.phaseAttribute);
    geometry.setAttribute('instanceAttackTime', this.attackTimeAttribute);

    // Create mesh for each faction
    for (const [factionId, colors] of Object.entries(FACTION_COLORS)) {
      const material = new THREE.ShaderMaterial({
        vertexShader: spearmanVertexShader,
        fragmentShader: spearmanFragmentShader,
        uniforms: {
          primaryColor: { value: colors.primary },
          secondaryColor: { value: colors.secondary },
          ambientLightColor: { value: new THREE.Color(0xffffff) },
          directionalLightColor: { value: new THREE.Color(0xffffff) },
          directionalLightDirection: { value: new THREE.Vector3(0.5, 1.0, 0.3).normalize() },
          uTime: { value: 0 },
        },
      });
      this.materials.set(factionId as FactionId, material);

      const mesh = new THREE.InstancedMesh(geometry, material, MAX_SPEARMEN);
      mesh.count = 0;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.frustumCulled = false;
      this.instancedMeshes.set(factionId as FactionId, mesh);
      this.scene.add(mesh);
    }
  }

  private createSpearmanGeometry(): THREE.BufferGeometry {
    const parts: THREE.BufferGeometry[] = [];
    const colors: THREE.Color[] = [];
    const animGroups: number[] = [];

    // ==================== HEAD ====================
    const head = new THREE.SphereGeometry(0.08, 10, 8);
    head.translate(0, 0.52, 0);
    parts.push(head);
    colors.push(COLORS.skin);
    animGroups.push(SPEARMAN_ANIM_GROUP.HEAD);

    // ==================== HELMET (Chinese style pointed helmet) ====================
    // Helmet dome
    const helmetDome = new THREE.SphereGeometry(
      0.09, 8, 6, 0, Math.PI * 2, 0, Math.PI * 0.55
    );
    helmetDome.translate(0, 0.54, 0);
    parts.push(helmetDome);
    colors.push(COLORS.spearBlue);
    animGroups.push(SPEARMAN_ANIM_GROUP.HEAD);

    // Helmet rim
    const helmetRim = new THREE.TorusGeometry(0.085, 0.01, 4, 12);
    helmetRim.rotateX(Math.PI / 2);
    helmetRim.translate(0, 0.54, 0);
    parts.push(helmetRim);
    colors.push(COLORS.gold);
    animGroups.push(SPEARMAN_ANIM_GROUP.HEAD);

    // Forehead plate
    const foreheadPlate = new THREE.BoxGeometry(0.06, 0.025, 0.012);
    foreheadPlate.translate(0, 0.52, 0.075);
    parts.push(foreheadPlate);
    colors.push(COLORS.gold);
    animGroups.push(SPEARMAN_ANIM_GROUP.HEAD);

    // Helmet spike (taller)
    const spike = new THREE.ConeGeometry(0.018, 0.08, 6);
    spike.translate(0, 0.68, 0);
    parts.push(spike);
    colors.push(COLORS.gold);
    animGroups.push(SPEARMAN_ANIM_GROUP.HEAD);

    // Spike base
    const spikeBase = new THREE.CylinderGeometry(0.022, 0.018, 0.015, 6);
    spikeBase.translate(0, 0.635, 0);
    parts.push(spikeBase);
    colors.push(COLORS.gold);
    animGroups.push(SPEARMAN_ANIM_GROUP.HEAD);

    // Neck guard
    const neckGuard = new THREE.CylinderGeometry(
      0.085, 0.095, 0.035, 8, 1, true, Math.PI * 0.25, Math.PI * 1.5
    );
    neckGuard.translate(0, 0.50, 0);
    parts.push(neckGuard);
    colors.push(COLORS.spearBlue);
    animGroups.push(SPEARMAN_ANIM_GROUP.HEAD);

    // ==================== TORSO (Heavy armor) ====================
    const torso = new THREE.CylinderGeometry(0.075, 0.085, 0.16, 8);
    torso.translate(0, 0.36, 0);
    parts.push(torso);
    colors.push(COLORS.spearBlue);
    animGroups.push(SPEARMAN_ANIM_GROUP.BODY);

    // Chest plate decoration (heart protector)
    const chestPlate = new THREE.CylinderGeometry(0.035, 0.035, 0.012, 8);
    chestPlate.rotateX(Math.PI / 2);
    chestPlate.translate(0, 0.38, 0.078);
    parts.push(chestPlate);
    colors.push(COLORS.bronze);
    animGroups.push(SPEARMAN_ANIM_GROUP.BODY);

    // Belt
    const belt = new THREE.CylinderGeometry(0.088, 0.088, 0.022, 8);
    belt.translate(0, 0.27, 0);
    parts.push(belt);
    colors.push(COLORS.gold);
    animGroups.push(SPEARMAN_ANIM_GROUP.BODY);

    // Belt buckle
    const buckle = new THREE.BoxGeometry(0.028, 0.022, 0.012);
    buckle.translate(0, 0.27, 0.085);
    parts.push(buckle);
    colors.push(COLORS.gold);
    animGroups.push(SPEARMAN_ANIM_GROUP.BODY);

    // Skirt armor
    const skirt = new THREE.CylinderGeometry(0.085, 0.11, 0.09, 8);
    skirt.translate(0, 0.195, 0);
    parts.push(skirt);
    colors.push(COLORS.spearBlue);
    animGroups.push(SPEARMAN_ANIM_GROUP.BODY);

    // Front armor plates
    for (let i = -1; i <= 1; i++) {
      const plate = new THREE.BoxGeometry(0.035, 0.06, 0.012);
      plate.translate(i * 0.04, 0.16, 0.10);
      parts.push(plate);
      colors.push(COLORS.spearBlue);
      animGroups.push(SPEARMAN_ANIM_GROUP.BODY);
    }

    // ==================== SHOULDER ARMOR ====================
    // Left shoulder
    const leftShoulder = new THREE.BoxGeometry(0.05, 0.02, 0.06);
    leftShoulder.translate(-0.095, 0.42, 0);
    parts.push(leftShoulder);
    colors.push(COLORS.spearBlue);
    animGroups.push(SPEARMAN_ANIM_GROUP.LEFT_SHOULDER);

    // Right shoulder
    const rightShoulder = new THREE.BoxGeometry(0.05, 0.02, 0.06);
    rightShoulder.translate(0.095, 0.42, 0);
    parts.push(rightShoulder);
    colors.push(COLORS.spearBlue);
    animGroups.push(SPEARMAN_ANIM_GROUP.RIGHT_SHOULDER);

    // Shoulder gold edges
    const leftShoulderEdge = new THREE.BoxGeometry(0.055, 0.008, 0.065);
    leftShoulderEdge.translate(-0.095, 0.435, 0);
    parts.push(leftShoulderEdge);
    colors.push(COLORS.gold);
    animGroups.push(SPEARMAN_ANIM_GROUP.LEFT_SHOULDER);

    const rightShoulderEdge = new THREE.BoxGeometry(0.055, 0.008, 0.065);
    rightShoulderEdge.translate(0.095, 0.435, 0);
    parts.push(rightShoulderEdge);
    colors.push(COLORS.gold);
    animGroups.push(SPEARMAN_ANIM_GROUP.RIGHT_SHOULDER);

    // ==================== SPEAR POSITION DEFINITION ====================
    const spearX = 0.12;  // Spear shaft X position
    const spearZ = 0.05;  // Spear shaft Z position (forward)

    // ==================== LEFT ARM (Lower grip) ====================
    const leftHandX = spearX;
    const leftHandY = 0.30;
    const leftHandZ = spearZ;

    const leftShoulderX = -0.10;
    const leftShoulderY = 0.40;
    const leftShoulderZ = 0;

    const leftElbowX = -0.02;
    const leftElbowY = 0.32;
    const leftElbowZ = 0.08;

    // Left upper arm
    const leftUpperArmDx = leftElbowX - leftShoulderX;
    const leftUpperArmDy = leftElbowY - leftShoulderY;
    const leftUpperArmDz = leftElbowZ - leftShoulderZ;
    const leftUpperArmLength = Math.sqrt(leftUpperArmDx * leftUpperArmDx + leftUpperArmDy * leftUpperArmDy + leftUpperArmDz * leftUpperArmDz);
    const leftUpperArm = new THREE.CylinderGeometry(0.028, 0.025, leftUpperArmLength, 6);
    const leftUpperArmAngleZ = Math.atan2(leftUpperArmDx, -leftUpperArmDy);
    const leftUpperArmAngleX = Math.atan2(leftUpperArmDz, Math.sqrt(leftUpperArmDx * leftUpperArmDx + leftUpperArmDy * leftUpperArmDy));
    leftUpperArm.rotateZ(leftUpperArmAngleZ);
    leftUpperArm.rotateX(leftUpperArmAngleX);
    leftUpperArm.translate(
      (leftShoulderX + leftElbowX) / 2,
      (leftShoulderY + leftElbowY) / 2,
      (leftShoulderZ + leftElbowZ) / 2
    );
    parts.push(leftUpperArm);
    colors.push(COLORS.spearBlue);
    animGroups.push(SPEARMAN_ANIM_GROUP.LEFT_ARM);

    // Left forearm
    const leftForearmDx = leftHandX - leftElbowX;
    const leftForearmDy = leftHandY - leftElbowY;
    const leftForearmDz = leftHandZ - leftElbowZ;
    const leftForearmLength = Math.sqrt(leftForearmDx * leftForearmDx + leftForearmDy * leftForearmDy + leftForearmDz * leftForearmDz);
    const leftForearm = new THREE.CylinderGeometry(0.025, 0.020, leftForearmLength, 6);
    const leftForearmAngleZ = Math.atan2(leftForearmDx, -leftForearmDy);
    const leftForearmAngleX = Math.atan2(leftForearmDz, Math.sqrt(leftForearmDx * leftForearmDx + leftForearmDy * leftForearmDy));
    leftForearm.rotateZ(leftForearmAngleZ);
    leftForearm.rotateX(leftForearmAngleX);
    leftForearm.translate(
      (leftElbowX + leftHandX) / 2,
      (leftElbowY + leftHandY) / 2,
      (leftElbowZ + leftHandZ) / 2
    );
    parts.push(leftForearm);
    colors.push(COLORS.spearBlue);
    animGroups.push(SPEARMAN_ANIM_GROUP.LEFT_ARM);

    // Left elbow joint
    const leftElbow = new THREE.SphereGeometry(0.026, 6, 5);
    leftElbow.translate(leftElbowX, leftElbowY, leftElbowZ);
    parts.push(leftElbow);
    colors.push(COLORS.spearBlue);
    animGroups.push(SPEARMAN_ANIM_GROUP.LEFT_ARM);

    // Left hand
    const leftHand = new THREE.SphereGeometry(0.024, 6, 5);
    leftHand.translate(leftHandX, leftHandY, leftHandZ);
    parts.push(leftHand);
    colors.push(COLORS.skin);
    animGroups.push(SPEARMAN_ANIM_GROUP.LEFT_ARM);

    // Left hand grip (fingers around spear)
    const leftGrip = new THREE.TorusGeometry(0.022, 0.010, 6, 8);
    leftGrip.rotateX(Math.PI / 2);
    leftGrip.translate(leftHandX, leftHandY, leftHandZ);
    parts.push(leftGrip);
    colors.push(COLORS.skin);
    animGroups.push(SPEARMAN_ANIM_GROUP.LEFT_ARM);

    // ==================== RIGHT ARM (Upper grip) ====================
    const rightHandX = spearX;
    const rightHandY = 0.42;
    const rightHandZ = spearZ;

    const rightShoulderX = 0.10;
    const rightShoulderY = 0.40;
    const rightShoulderZ = 0;

    const rightElbowX = 0.14;
    const rightElbowY = 0.38;
    const rightElbowZ = 0.08;

    // Right upper arm
    const rightUpperArmDx = rightElbowX - rightShoulderX;
    const rightUpperArmDy = rightElbowY - rightShoulderY;
    const rightUpperArmDz = rightElbowZ - rightShoulderZ;
    const rightUpperArmLength = Math.sqrt(rightUpperArmDx * rightUpperArmDx + rightUpperArmDy * rightUpperArmDy + rightUpperArmDz * rightUpperArmDz);
    const rightUpperArm = new THREE.CylinderGeometry(0.028, 0.025, rightUpperArmLength, 6);
    const rightUpperArmAngleZ = Math.atan2(rightUpperArmDx, -rightUpperArmDy);
    const rightUpperArmAngleX = Math.atan2(rightUpperArmDz, Math.sqrt(rightUpperArmDx * rightUpperArmDx + rightUpperArmDy * rightUpperArmDy));
    rightUpperArm.rotateZ(rightUpperArmAngleZ);
    rightUpperArm.rotateX(rightUpperArmAngleX);
    rightUpperArm.translate(
      (rightShoulderX + rightElbowX) / 2,
      (rightShoulderY + rightElbowY) / 2,
      (rightShoulderZ + rightElbowZ) / 2
    );
    parts.push(rightUpperArm);
    colors.push(COLORS.spearBlue);
    animGroups.push(SPEARMAN_ANIM_GROUP.RIGHT_ARM);

    // Right forearm
    const rightForearmDx = rightHandX - rightElbowX;
    const rightForearmDy = rightHandY - rightElbowY;
    const rightForearmDz = rightHandZ - rightElbowZ;
    const rightForearmLength = Math.sqrt(rightForearmDx * rightForearmDx + rightForearmDy * rightForearmDy + rightForearmDz * rightForearmDz);
    const rightForearm = new THREE.CylinderGeometry(0.025, 0.020, rightForearmLength, 6);
    const rightForearmAngleZ = Math.atan2(rightForearmDx, -rightForearmDy);
    const rightForearmAngleX = Math.atan2(rightForearmDz, Math.sqrt(rightForearmDx * rightForearmDx + rightForearmDy * rightForearmDy));
    rightForearm.rotateZ(rightForearmAngleZ);
    rightForearm.rotateX(rightForearmAngleX);
    rightForearm.translate(
      (rightElbowX + rightHandX) / 2,
      (rightElbowY + rightHandY) / 2,
      (rightElbowZ + rightHandZ) / 2
    );
    parts.push(rightForearm);
    colors.push(COLORS.spearBlue);
    animGroups.push(SPEARMAN_ANIM_GROUP.RIGHT_ARM);

    // Right elbow joint
    const rightElbow = new THREE.SphereGeometry(0.026, 6, 5);
    rightElbow.translate(rightElbowX, rightElbowY, rightElbowZ);
    parts.push(rightElbow);
    colors.push(COLORS.spearBlue);
    animGroups.push(SPEARMAN_ANIM_GROUP.RIGHT_ARM);

    // Right hand
    const rightHand = new THREE.SphereGeometry(0.024, 6, 5);
    rightHand.translate(rightHandX, rightHandY, rightHandZ);
    parts.push(rightHand);
    colors.push(COLORS.skin);
    animGroups.push(SPEARMAN_ANIM_GROUP.RIGHT_ARM);

    // Right hand grip
    const rightGrip = new THREE.TorusGeometry(0.022, 0.010, 6, 8);
    rightGrip.rotateX(Math.PI / 2);
    rightGrip.translate(rightHandX, rightHandY, rightHandZ);
    parts.push(rightGrip);
    colors.push(COLORS.skin);
    animGroups.push(SPEARMAN_ANIM_GROUP.RIGHT_ARM);

    // Wrist guards
    const leftWristGuard = new THREE.CylinderGeometry(0.026, 0.024, 0.025, 6);
    leftWristGuard.translate(
      (leftElbowX + leftHandX) / 2 + 0.02,
      (leftElbowY + leftHandY) / 2,
      (leftElbowZ + leftHandZ) / 2
    );
    parts.push(leftWristGuard);
    colors.push(COLORS.bronze);
    animGroups.push(SPEARMAN_ANIM_GROUP.LEFT_ARM);

    const rightWristGuard = new THREE.CylinderGeometry(0.026, 0.024, 0.025, 6);
    rightWristGuard.translate(
      (rightElbowX + rightHandX) / 2,
      (rightElbowY + rightHandY) / 2,
      (rightElbowZ + rightHandZ) / 2
    );
    parts.push(rightWristGuard);
    colors.push(COLORS.bronze);
    animGroups.push(SPEARMAN_ANIM_GROUP.RIGHT_ARM);

    // ==================== LEGS ====================
    // Left thigh
    const leftThigh = new THREE.CylinderGeometry(0.035, 0.030, 0.08, 6);
    leftThigh.translate(-0.038, 0.11, 0);
    parts.push(leftThigh);
    colors.push(COLORS.spearBlue);
    animGroups.push(SPEARMAN_ANIM_GROUP.LEFT_LEG);

    // Left shin
    const leftShin = new THREE.CylinderGeometry(0.030, 0.024, 0.07, 6);
    leftShin.translate(-0.038, 0.04, 0);
    parts.push(leftShin);
    colors.push(COLORS.spearBlue);
    animGroups.push(SPEARMAN_ANIM_GROUP.LEFT_LEG);

    // Left foot
    const leftFoot = new THREE.BoxGeometry(0.038, 0.018, 0.052);
    leftFoot.translate(-0.038, 0.009, 0.008);
    parts.push(leftFoot);
    colors.push(COLORS.iron);
    animGroups.push(SPEARMAN_ANIM_GROUP.LEFT_LEG);

    // Right thigh
    const rightThigh = new THREE.CylinderGeometry(0.035, 0.030, 0.08, 6);
    rightThigh.translate(0.038, 0.11, 0);
    parts.push(rightThigh);
    colors.push(COLORS.spearBlue);
    animGroups.push(SPEARMAN_ANIM_GROUP.RIGHT_LEG);

    // Right shin
    const rightShin = new THREE.CylinderGeometry(0.030, 0.024, 0.07, 6);
    rightShin.translate(0.038, 0.04, 0);
    parts.push(rightShin);
    colors.push(COLORS.spearBlue);
    animGroups.push(SPEARMAN_ANIM_GROUP.RIGHT_LEG);

    // Right foot
    const rightFoot = new THREE.BoxGeometry(0.038, 0.018, 0.052);
    rightFoot.translate(0.038, 0.009, 0.008);
    parts.push(rightFoot);
    colors.push(COLORS.iron);
    animGroups.push(SPEARMAN_ANIM_GROUP.RIGHT_LEG);

    // ==================== WEAPON: SPEAR (红缨枪) ====================
    const shaftLength = 0.90;
    const shaftBottom = 0.05;
    const shaftCenter = shaftBottom + shaftLength / 2;

    // Spear shaft
    const shaft = new THREE.CylinderGeometry(0.014, 0.018, shaftLength, 8);
    shaft.translate(spearX, shaftCenter, spearZ);
    parts.push(shaft);
    colors.push(COLORS.wood);
    animGroups.push(SPEARMAN_ANIM_GROUP.SPEAR);

    // Shaft reinforcement rings (bronze)
    for (const y of [0.20, 0.45, 0.70]) {
      const ring = new THREE.TorusGeometry(0.018, 0.005, 4, 10);
      ring.rotateX(Math.PI / 2);
      ring.translate(spearX, y, spearZ);
      parts.push(ring);
      colors.push(COLORS.bronze);
      animGroups.push(SPEARMAN_ANIM_GROUP.SPEAR);
    }

    // ==================== SPEAR TIP ====================
    const spearTipBase = shaftBottom + shaftLength;

    // Spear tip collar/neck
    const collar = new THREE.CylinderGeometry(0.022, 0.016, 0.025, 8);
    collar.translate(spearX, spearTipBase + 0.012, spearZ);
    parts.push(collar);
    colors.push(COLORS.bronze);
    animGroups.push(SPEARMAN_ANIM_GROUP.SPEAR);

    // Main spear head
    const spearHeadLength = 0.14;
    const spearHead = new THREE.ConeGeometry(0.025, spearHeadLength, 12);
    spearHead.translate(spearX, spearTipBase + 0.025 + spearHeadLength / 2, spearZ);
    parts.push(spearHead);
    colors.push(COLORS.steel);
    animGroups.push(SPEARMAN_ANIM_GROUP.SPEAR);

    // Blade edge decorations
    const bladeEdge1 = new THREE.BoxGeometry(0.003, spearHeadLength * 0.7, 0.035);
    bladeEdge1.translate(spearX, spearTipBase + 0.06, spearZ);
    parts.push(bladeEdge1);
    colors.push(new THREE.Color(0xc0c0c0));
    animGroups.push(SPEARMAN_ANIM_GROUP.SPEAR);

    const bladeEdge2 = new THREE.BoxGeometry(0.035, spearHeadLength * 0.7, 0.003);
    bladeEdge2.translate(spearX, spearTipBase + 0.06, spearZ);
    parts.push(bladeEdge2);
    colors.push(new THREE.Color(0xc0c0c0));
    animGroups.push(SPEARMAN_ANIM_GROUP.SPEAR);

    // Sharp tip
    const sharpTip = new THREE.ConeGeometry(0.008, 0.04, 8);
    sharpTip.translate(spearX, spearTipBase + 0.025 + spearHeadLength + 0.02, spearZ);
    parts.push(sharpTip);
    colors.push(new THREE.Color(0xe8e8e8));
    animGroups.push(SPEARMAN_ANIM_GROUP.SPEAR);

    // ==================== RED TASSEL (红缨) ====================
    const tasselY = spearTipBase;

    // Tassel base
    const tasselBase = new THREE.SphereGeometry(0.020, 8, 6);
    tasselBase.translate(spearX, tasselY, spearZ);
    parts.push(tasselBase);
    colors.push(COLORS.tassel);
    animGroups.push(SPEARMAN_ANIM_GROUP.TASSEL);

    // Tassel strands
    for (let i = 0; i < 12; i++) {
      const strandLength = 0.06 + Math.random() * 0.02;
      const strand = new THREE.CylinderGeometry(0.004, 0.002, strandLength, 4);
      const angle = (i / 12) * Math.PI * 2;
      const swayAngle = 0.5 + Math.random() * 0.2;
      strand.rotateZ(swayAngle);
      strand.rotateY(angle);
      strand.translate(
        spearX + Math.cos(angle) * 0.018,
        tasselY - strandLength / 2 - 0.01,
        spearZ + Math.sin(angle) * 0.018
      );
      parts.push(strand);
      colors.push(COLORS.tassel);
      animGroups.push(SPEARMAN_ANIM_GROUP.TASSEL);
    }

    // Tassel tips
    for (let i = 0; i < 4; i++) {
      const tip = new THREE.SphereGeometry(0.006, 4, 3);
      const angle = (i / 4) * Math.PI * 2 + Math.PI / 4;
      tip.translate(
        spearX + Math.cos(angle) * 0.025,
        tasselY - 0.07,
        spearZ + Math.sin(angle) * 0.025
      );
      parts.push(tip);
      colors.push(COLORS.tassel);
      animGroups.push(SPEARMAN_ANIM_GROUP.TASSEL);
    }

    // ==================== SPEAR BUTT CAP ====================
    const buttCap = new THREE.ConeGeometry(0.018, 0.04, 8);
    buttCap.rotateX(Math.PI);
    buttCap.translate(spearX, shaftBottom - 0.02, spearZ);
    parts.push(buttCap);
    colors.push(COLORS.bronze);
    animGroups.push(SPEARMAN_ANIM_GROUP.SPEAR);

    return this.mergeGeometriesWithAnimData(parts, colors, animGroups);
  }

  private mergeGeometriesWithAnimData(
    geometries: THREE.BufferGeometry[],
    colors: THREE.Color[],
    animGroups: number[]
  ): THREE.BufferGeometry {
    // Calculate total vertices
    let totalVertices = 0;
    for (const geo of geometries) {
      const nonIndexed = geo.index ? geo.toNonIndexed() : geo;
      totalVertices += nonIndexed.getAttribute('position').count;
    }

    // Create output arrays
    const positions = new Float32Array(totalVertices * 3);
    const normals = new Float32Array(totalVertices * 3);
    const vertexColors = new Float32Array(totalVertices * 3);
    const animGroupAttr = new Float32Array(totalVertices);

    let offset = 0;
    for (let i = 0; i < geometries.length; i++) {
      let geo = geometries[i];
      if (geo.index) {
        geo = geo.toNonIndexed();
      }

      const posAttr = geo.getAttribute('position');
      const normalAttr = geo.getAttribute('normal');
      const color = colors[i];
      const animGroup = animGroups[i];
      const count = posAttr.count;

      // Add subtle color variation
      const variation = 0.94 + Math.random() * 0.12;

      for (let j = 0; j < count; j++) {
        const idx = offset + j;

        // Position
        positions[idx * 3] = posAttr.getX(j);
        positions[idx * 3 + 1] = posAttr.getY(j);
        positions[idx * 3 + 2] = posAttr.getZ(j);

        // Normal
        if (normalAttr) {
          normals[idx * 3] = normalAttr.getX(j);
          normals[idx * 3 + 1] = normalAttr.getY(j);
          normals[idx * 3 + 2] = normalAttr.getZ(j);
        }

        // Color with variation
        vertexColors[idx * 3] = color.r * variation;
        vertexColors[idx * 3 + 1] = color.g * variation;
        vertexColors[idx * 3 + 2] = color.b * variation;

        // Animation group
        animGroupAttr[idx] = animGroup;
      }

      offset += count;
      geometries[i].dispose();
    }

    // Create merged geometry
    const merged = new THREE.BufferGeometry();
    merged.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    merged.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
    merged.setAttribute('color', new THREE.BufferAttribute(vertexColors, 3));
    merged.setAttribute('animGroup', new THREE.BufferAttribute(animGroupAttr, 1));
    merged.computeVertexNormals();

    return merged;
  }

  addSpearman(entity: Entity, x: number, y: number, z: number, factionId: FactionId): void {
    if (this.entityMap.has(entity)) return;

    const data: SpearmanData = {
      entity,
      x,
      y,
      z,
      factionId,
      rotation: 0,
      speed: 0,
      animPhase: Math.random() * Math.PI * 2,
      attackTime: -1,
      attackDuration: 0.5,
    };
    this.spearmanData.push(data);
    this.entityMap.set(entity, data);
  }

  removeSpearman(entity: Entity): void {
    const spearman = this.entityMap.get(entity);
    if (!spearman) return;

    const index = this.spearmanData.indexOf(spearman);
    if (index !== -1) {
      const last = this.spearmanData[this.spearmanData.length - 1];
      this.spearmanData[index] = last;
      this.spearmanData.pop();
    }
    this.entityMap.delete(entity);
  }

  updateSpearman(entity: Entity, x: number, y: number, z: number, rotation?: number): void {
    const spearman = this.entityMap.get(entity);
    if (!spearman) return;

    // Calculate speed from movement
    const dx = x - spearman.x;
    const dz = z - spearman.z;
    const distance = Math.sqrt(dx * dx + dz * dz);

    // Smooth speed transition
    const targetSpeed = Math.min(distance * 30, 1.0);
    spearman.speed = spearman.speed * 0.85 + targetSpeed * 0.15;

    // Update rotation
    if (rotation !== undefined) {
      spearman.rotation = rotation;
    } else if (distance > 0.001) {
      spearman.rotation = Math.atan2(dx, dz);
    }

    spearman.x = x;
    spearman.y = y;
    spearman.z = z;
  }

  update(deltaTime: number): void {
    this.time += deltaTime;

    // Update time uniform for all materials
    for (const material of this.materials.values()) {
      material.uniforms.uTime.value = this.time;
    }

    // Group spearmen by faction for instanced rendering
    const factionGroups = new Map<FactionId, SpearmanData[]>();
    for (const spearman of this.spearmanData) {
      if (!factionGroups.has(spearman.factionId)) {
        factionGroups.set(spearman.factionId, []);
      }
      factionGroups.get(spearman.factionId)!.push(spearman);
    }

    // Update each faction's instanced mesh
    for (const [factionId, mesh] of this.instancedMeshes) {
      const spearmenList = factionGroups.get(factionId) || [];
      mesh.count = spearmenList.length;

      if (spearmenList.length === 0) continue;

      // Update instance attributes and matrices
      for (let i = 0; i < spearmenList.length; i++) {
        const spearman = spearmenList[i];

        // Update instance attributes
        if (this.speedAttribute) {
          this.speedAttribute.setX(i, spearman.speed);
        }
        if (this.phaseAttribute) {
          this.phaseAttribute.setX(i, spearman.animPhase);
        }
        if (this.attackTimeAttribute) {
          this.attackTimeAttribute.setX(i, spearman.attackTime);
        }

        // Update transform matrix
        const surfaceOffset = 0.02;
        this.dummy.position.set(spearman.x, spearman.y + surfaceOffset, spearman.z);
        this.dummy.rotation.set(0, spearman.rotation, 0);
        this.dummy.scale.setScalar(1.75);
        this.dummy.updateMatrix();
        mesh.setMatrixAt(i, this.dummy.matrix);
      }

      // Mark for GPU update
      mesh.instanceMatrix.needsUpdate = true;
      if (this.speedAttribute) this.speedAttribute.needsUpdate = true;
      if (this.phaseAttribute) this.phaseAttribute.needsUpdate = true;
      if (this.attackTimeAttribute) this.attackTimeAttribute.needsUpdate = true;
    }
  }

  getSpearmanCount(): number {
    return this.spearmanData.length;
  }

  clear(): void {
    this.spearmanData.length = 0;
    for (const mesh of this.instancedMeshes.values()) {
      mesh.count = 0;
    }
  }

  dispose(): void {
    // Remove event listeners
    if (this.attackEventHandler) {
      EventBus.off('unit:attack', this.attackEventHandler);
    }

    // Dispose meshes
    for (const mesh of this.instancedMeshes.values()) {
      mesh.geometry.dispose();
      if (mesh.material instanceof THREE.Material) {
        mesh.material.dispose();
      }
      this.scene.remove(mesh);
    }
    this.instancedMeshes.clear();
    this.materials.clear();
    this.spearmanData.length = 0;
  }
}
